import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { getHttpStatus } from "@/utils/httpErrors";
import { PageState } from "@/components/common/PageState";
import { ChannelsList } from "./components/ChannelsList";
import { useMessagingChannelConfigs } from "./hooks/messaging-channel-configs.hooks";

export const CommunicationChannelsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const hotelId = useSelectedHotelId();
  const { data, isLoading, isError, error } =
    useMessagingChannelConfigs(hotelId);

  if (!hotelId) {
    return <PageState text={t("communicationChannels.page.noHotelSelected")} />;
  }

  if (isLoading) {
    return <PageState text={t("communicationChannels.matrix.loading")} />;
  }

  if (isError) {
    if (getHttpStatus(error) === 503) {
      return (
        <div className="flex flex-col gap-6 p-12 max-w-4xl">
          <header className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold text-foreground">
              {t("communicationChannels.matrix.title")}
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              {t("communicationChannels.matrix.description")}
            </p>
          </header>
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {t("communicationChannels.killSwitch")}
          </div>
        </div>
      );
    }
    return (
      <PageState
        text={t("communicationChannels.matrix.loadError")}
        tone="error"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 p-12 max-w-4xl">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("communicationChannels.matrix.title")}
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          {t("communicationChannels.matrix.description")}
        </p>
        <Link
          to="/communication-integrations"
          className="inline-flex items-center gap-1 text-sm text-skyline-2 hover:underline w-fit"
        >
          {t("communicationChannels.matrix.manageIntegrationsLink")}
          <ExternalLink className="size-3.5" />
        </Link>
      </header>

      <ChannelsList hotelId={hotelId} configs={data ?? []} />
    </div>
  );
};
